import { useState, useEffect } from 'react';
import axios from 'axios';
import { StickyNote, Loader2, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ClientNotes = ({ clientId, token }) => {
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newNoteText, setNewNoteText] = useState('');
    const [creating, setCreating] = useState(false);

    useEffect(() => {
        if (clientId) {
            fetchNotes();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId]);

    async function fetchNotes() {
        try {
            const res = await axios.get(`${API}/clients/${clientId}/notes`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setNotes(res.data);
        } catch (error) {
            console.error('Error fetching notes:', error);
            toast.error('Error al cargar notas');
        } finally {
            setLoading(false);
        }
    }

    const handleCreateNote = async () => {
        if (!newNoteText.trim()) {
            toast.error('Escribe una nota');
            return;
        }

        setCreating(true);
        try {
            await axios.post(`${API}/clients/${clientId}/notes`, {
                client_id: clientId,
                text: newNoteText.trim()
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            toast.success('Nota agregada');
            setNewNoteText('');
            await fetchNotes();
        } catch (error) {
            console.error('Error creating note:', error);
            toast.error('Error al crear nota');
        } finally {
            setCreating(false);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Hace un momento';
        if (diffMins < 60) return `Hace ${diffMins} min`;
        if (diffHours < 24) return `Hace ${diffHours}h`;
        if (diffDays < 7) return `Hace ${diffDays}d`;

        return date.toLocaleDateString('es-MX', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400 mx-auto" />
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg border border-gray-200">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                    Notas Internas ({notes.length})
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                    Notas visibles solo para el equipo interno
                </p>
            </div>

            {/* Add Note Form */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div className="space-y-3">
                    <Textarea
                        value={newNoteText}
                        onChange={(e) => setNewNoteText(e.target.value)}
                        placeholder="Escribe una nota interna..."
                        disabled={creating}
                        rows={3}
                        className="resize-none"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                                handleCreateNote();
                            }
                        }}
                    />
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">
                            Presiona Cmd/Ctrl + Enter para enviar
                        </span>
                        <Button
                            onClick={handleCreateNote}
                            disabled={creating || !newNoteText.trim()}
                            size="sm"
                            className="bg-gray-900 hover:bg-gray-800"
                        >
                            {creating ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Guardando...
                                </>
                            ) : (
                                <>
                                    <Send className="mr-2 h-4 w-4" />
                                    Agregar Nota
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Notes Timeline */}
            {notes.length === 0 ? (
                <div className="p-12 text-center">
                    <StickyNote className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 mb-2">No hay notas internas</p>
                    <p className="text-sm text-gray-400">Agrega la primera nota para este cliente</p>
                </div>
            ) : (
                <div className="divide-y divide-gray-200">
                    {notes.map(note => (
                        <div key={note.id} className="px-6 py-4 hover:bg-gray-50">
                            <div className="flex items-start gap-3">
                                {/* Avatar */}
                                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-medium text-sm flex-shrink-0">
                                    {note.author_user_name ? note.author_user_name.charAt(0).toUpperCase() : 'U'}
                                </div>

                                {/* Note Content */}
                                <div className="flex-1 min-w-0">
                                    {/* Author and Date */}
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-sm font-medium text-gray-900">
                                            {note.author_user_name || 'Usuario'}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                            {formatDate(note.created_at)}
                                        </span>
                                    </div>

                                    {/* Note Text */}
                                    <div className="text-sm text-gray-700 whitespace-pre-wrap break-words">
                                        {note.text}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
